<template>
	<div class="btn-attached-wrap">
		<button class="btn-attached" @click="openFileDialog">
			<svg-icon name="attachedFile" :size="24" />
		</button>
		<input
			ref="fileInput"
			type="file"
			:accept="acceptFormats"
			class="display-none"
			style="display: none"
			@change="handleFileUpload"
		/>
	</div>
</template>

<script setup lang="ts">
import SvgIcon from '@/components/SvgIcon.vue'
import { useThreadStore } from '@/stores/thread'
import { useComposerStore } from '@/stores/composer'
import { hasTextInPDF } from '@/helpers/pdfTypeDeterminant'
import { getFileTypeFromName } from '@/helpers/fileTypeDeterminant'
import { isAllowedImageFile, INVALID_IMAGE_FORMAT_MESSAGE } from '@/helpers/imageFormats'
import AppConfig from '@/config'
import { computed, ref } from 'vue'

const fileInput = ref<HTMLInputElement | null>(null)
const threadStore = useThreadStore()
const composerStore = useComposerStore()
const emit = defineEmits<{
	'attach-error': [message: string]
	'format-error': [message: string]
}>()

const imageEditMode = computed(() => threadStore.imageEditMode)
const acceptFormats = computed(() =>
	imageEditMode.value ? '.jpg, .jpeg, .png, .webp' : '.doc, .docx, .pdf, .ppt, .pptx, .txt'
)

function openFileDialog() {
	fileInput.value?.click()
}

async function handleFileUpload(event: Event) {
	const input = event.target as HTMLInputElement
	threadStore.clearErrorMessage()
	composerStore.clearCurrentFile()
	emit('attach-error', '')
	emit('format-error', '')
	if (input.files && input.files.length > 0) {
		const file = input.files[0]
		if (!checkFileSize(file)) {
			emit('attach-error', `Загрузите файл меньше ${AppConfig.maxSizeMB} MB`)
			clearInput()
			return
		}
		if (imageEditMode.value && !isAllowedImageFile(file)) {
			// Показываем превью прикреплённого файла и ошибку формата рядом с ним
			composerStore.setCurrentFile(file)
			emit('format-error', INVALID_IMAGE_FORMAT_MESSAGE)
			clearInput()
			return
		}
		if (!imageEditMode.value && getFileTypeFromName(file.name) === 'pdf') {
			const containsText = await hasTextInPDF(file)
			if (!containsText) {
				emit('attach-error', 'В файле нет текста. Загрузите корректный файл.')
				clearInput()
				return
			}
		}
		composerStore.setCurrentFile(file)
		clearInput()
	}
}

function checkFileSize(file: File): boolean {
	const maxSizeBytes = AppConfig.maxSizeMB * 1024 * 1024
	return file.size <= maxSizeBytes
}
function clearInput() {
	if (fileInput.value) {
		fileInput.value.value = ''
	}
}

defineExpose({
	clearInput
})
</script>

<style lang="scss" scoped>
.btn-attached {
	background: none;
	border: none;
	padding: 0;
	color: var(--icon-action);
	cursor: pointer;
	&-wrap {
		display: inline;
	}
	&:hover {
		color: var(--text-brand);
	}
	&:active {
		color: var(--text-brand);
	}
}
</style>
